import React, {Suspense} from "react";
import {useState} from "react";
import {Swiper, SwiperSlide} from "swiper/react";
import {Navigation, Pagination, Autoplay, A11y, EffectFade} from "swiper";

import {Button} from "../../../components/Button";
import {Grid} from "../../../components/Grid/Grid";
import MediaBlockElement from "../MediaBlockElement";

import {
    useIsBigScreen,
    useIsDesktop, useIsMobileLandscape,
    useIsTiny
} from "../../../lib/hooks/mediaQueries";

import {generateClassString as gcs} from "../../../lib/helper/generateClassString";

import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import "swiper/css/effect-fade";

import * as styles from "./component.module.less";
import {startTransition} from "react";
import {useMediaQuery} from "react-responsive";

const MediaGallery = React.lazy(() =>
    import("../../../components/media-gallery/MediaGallery").then((module) => {
        return {default: module.MediaGallery};
    })
);

export default function HeroLayoutElement({
                                              mediaBlock,
                                              mediaLayout,
                                              hasLightBackground = true,
                                              galleryAssets,
                                              thumbnails
                                          }) {
    const [currentIndex, setCurrentIndex] = useState(0);

    const withGallery = mediaLayout !== "standard_without_lightbox";

    const idButtonPrev = `button-prev-${Math.floor(Math.random() * 1000)}`;
    const idButtonNext = `button-next-${Math.floor(Math.random() * 1000)}`;

    const isDesktop = useIsDesktop();
    const isTiny = useIsTiny();
    const isBigScreen = useIsBigScreen();
    const isMobileLandscape = useIsMobileLandscape();
    const isMediumScreen = useMediaQuery({ query: "(min-width: 1199px) and (max-width: 1538px)" });

    const [galleryOpen, setGalleryOpen] = useState(false);

    function toggleGallery() {
        if (galleryOpen) {
            setGalleryOpen(false);
            return;
        }

        startTransition(() => {
            setGalleryOpen(true);
        });
    }

    return (
        <>
            {mediaLayout === "standard_with_slider" ? (
                ((mediaBlock.length > 1) && (!isMobileLandscape)) ? (
                    <div className={styles.heroMediaLayout}>
                        <Grid
                            className={styles.heroGrid}
                            size={
                                isMediumScreen //(min-width: 1199px) and (max-width: 1538px)
                                ? "l"
                                : isTiny //max-width: 450px
                                ? "s"
                                : isDesktop //max-width: 1200px
                                ? "m"
                                : isBigScreen //min-width: 1539px
                                ? "xl"
                                : "s"
                            }

                        />
                        <Swiper
                            modules={[A11y, EffectFade, Autoplay]}
                            loop={true}
                            effect="fade"
                            fadeEffect={{
                                crossFade: true
                            }}
                            autoplay
                        >
                            {mediaBlock.map((mediaElement) => {
                                return (
                                    <SwiperSlide
                                        className={styles.heroMediaSlide}
                                        key={mediaElement.id}
                                    >
                                        <MediaBlockElement
                                            mediaElement={mediaElement}
                                            withGallery={withGallery}
                                        />
                                    </SwiperSlide>
                                );
                            })}
                        </Swiper>
                    </div>
                ) : (
                    <div className={styles.heroMediaLayout}>
                        <Grid
                            className={styles.heroGrid}
                            size={
                                isMediumScreen
                                ? "l"
                                : isTiny
                                ? "s"
                                : isDesktop
                                ? "m"
                                : isBigScreen
                                ? "xl"
                                : "s"
                            }
                        />
                        <div className={styles.heroMediaSlide}>
                            <MediaBlockElement mediaElement={mediaBlock[0]}/>
                        </div>
                    </div>
                )
            ) : mediaLayout === "big_picture" ? (
                ((mediaBlock.length > 1) && (!isMobileLandscape)) ? (
                    <div
                        className={gcs(
                            styles.heroMediaLayout,
                            styles.bigPictureSliderWrap
                        )}
                    >
                        <Grid
                            className={gcs(
                                styles.heroGrid,
                                styles.bigPictureGrid
                            )}
                            size={
                                isMediumScreen
                                ? "l"
                                : isTiny
                                ? "s"
                                : isDesktop
                                ? "m"
                                : isBigScreen
                                ? "xl"
                                : "s"
                            }
                        />
                        <Swiper
                            modules={[Autoplay, EffectFade]}
                            loop={true}
                            effect="fade"
                            fadeEffect={{
                                crossFade: true
                            }}
                            autoplay={{delay: 5000}}
                            className={styles.bigPictureSlider}
                        >
                            {mediaBlock.map((mediaElement) => {
                                return (
                                    <SwiperSlide
                                        className={styles.bigPictureInSlider}
                                        key={mediaElement.id}
                                    >
                                        <MediaBlockElement
                                            mediaElement={mediaElement}
                                        />
                                    </SwiperSlide>
                                );
                            })}
                        </Swiper>
                    </div>
                ) : (
                    <div
                        className={gcs(
                            styles.heroMediaLayout,
                            styles.bigPictureWrap
                        )}
                    >
                        <Grid
                            className={gcs(
                                styles.heroGrid,
                                styles.bigPictureGrid
                            )}
                            size={
                                isMediumScreen
                                ? "l"
                                : isTiny
                                ? "s"
                                : isDesktop
                                ? "m"
                                : isBigScreen
                                ? "xl"
                                : "s"
                            }
                        />
                        <div className={styles.bigPicture}>
                            <MediaBlockElement mediaElement={mediaBlock[0]}/>
                        </div>
                    </div>
                )
            ) : mediaBlock.length > 1 ? (
                <div className={styles.heroMediaLayout}>
                    <Grid
                        className={styles.heroGrid}
                        size={
                            isMediumScreen
                            ? "l"
                            : isTiny
                            ? "s"
                            : isDesktop
                            ? "m"
                            : isBigScreen
                            ? "xl"
                            : "s"
                        }
                    />
                    <div>
                        <Swiper
                            modules={[Navigation, Pagination, A11y, EffectFade]}
                            allowSlidePrev={true}
                            centeredSlides={true}
                            effect="fade"
                            fadeEffect={{
                                crossFade: true
                            }}
                            navigation={{
                                prevEl: `#${idButtonPrev}`,
                                nextEl: `#${idButtonNext}`
                            }}
                            onActiveIndexChange={(index) => {
                                setCurrentIndex(index.realIndex + 1);
                            }}
                            loop
                        >
                            {mediaBlock.map((mediaElement) => {
                                return (
                                    <SwiperSlide
                                        className={styles.heroMediaSlide}
                                        key={mediaElement.id}
                                    >
                                        {mediaLayout === "standard" && (
                                            <>
                                                <button
                                                    className={styles.library}
                                                    aria-label="library"
                                                    onClick={toggleGallery}
                                                />
                                            </>
                                        )}
                                        <MediaBlockElement
                                            withGallery={withGallery}
                                            mediaElement={mediaElement}
                                        />
                                    </SwiperSlide>
                                );
                            })}
                        </Swiper>

                        <div
                            className={gcs(
                                styles.sliderControls,
                                !hasLightBackground ? styles.lightColor : ""
                            )}
                        >
                            <div className={styles.paginationElement}>
                                {`${currentIndex} | ${mediaBlock.length}`}
                            </div>

                            <Button
                                id={idButtonPrev}
                                type="prev"
                                icon="arrowLeft"
                                aria-label="prev"
                            />

                            <Button
                                id={idButtonNext}
                                type="next"
                                icon="arrowRight"
                                aria-label="next"
                            />
                        </div>
                    </div>

                    <Suspense fallback={<div>Loading...</div>}>
                        {galleryOpen && (
                            <MediaGallery
                                toggleFunction={toggleGallery}
                                galleryImages={galleryAssets}
                                thumbnails={thumbnails}
                                initialElement={currentIndex - 1}
                            />
                        )}
                    </Suspense>
                </div>
            ) : (
                <div className={styles.heroMediaLayout}>
                    <Grid
                        className={styles.heroGrid}
                        size={
                            isMediumScreen
                            ? "l"
                            : isTiny
                            ? "s"
                            : isDesktop
                            ? "m"
                            : isBigScreen
                            ? "xl"
                            : "s"
                        }
                    />
                    <div className={styles.heroMediaSlide}>
                        {mediaLayout === "standard" && (
                            <>
                                <button
                                    className={styles.library}
                                    aria-label="library"
                                    onClick={toggleGallery}
                                />
                            </>
                        )}
                        <MediaBlockElement mediaElement={mediaBlock[0]}/>
                    </div>

                    {galleryOpen && (
                        <MediaGallery
                            toggleFunction={toggleGallery}
                            galleryImages={galleryAssets}
                            thumbnails={thumbnails}
                            initialElement={0}
                        />
                    )}
                </div>
            )}
        </>
    );
}
